import React, { useEffect, useState } from "react";

import {
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  startOfWeek,
} from "date-fns";

import { color, spacing } from "../../tokens";
import { Box } from "../Box/Box";
import { Button } from "../Button/Button";
import { Icon } from "../Icon/Icon";
import { Popover } from "../Popover/Popover";
import { Typography } from "../Typography/Typography";
import { useEventEmitter } from "../useEventEmitter";
import { CalendarDay } from "./CalendarDay";
import { CalendarHeader } from "./CalendarHeader";
import { DatePickerProps, TempRange } from "./types";

const WEEK_DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export const DatePicker: React.FC<DatePickerProps> = (props) => {
  const { mode, placeholder = "Select date", disabled = false } = props;

  const closePopoverEvent = useEventEmitter<void>();

  const singleValue = mode === "single" ? props.value ?? null : null;
  const rangeStart = mode === "range" ? props.startDate ?? null : null;
  const rangeEnd = mode === "range" ? props.endDate ?? null : null;

  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    if (mode === "single" && props.value) {
      return new Date(props.value);
    }
    if (mode === "range" && (props.startDate || props.endDate)) {
      return new Date((props.endDate ?? props.startDate)!);
    }
    return new Date();
  });

  const [tempSingle, setTempSingle] = useState<Date | null>(singleValue);
  const [tempRange, setTempRange] = useState<TempRange>({
    start: rangeStart,
    end: rangeEnd,
  });
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  useEffect(() => {
    if (mode !== "single") {
      return;
    }
    setTempSingle(singleValue);
    if (singleValue) {
      setCurrentMonth(new Date(singleValue));
    }
  }, [mode, singleValue]);

  useEffect(() => {
    if (mode !== "range") {
      return;
    }
    setTempRange({ start: rangeStart, end: rangeEnd });
    const anchor = rangeEnd ?? rangeStart;
    if (anchor) {
      setCurrentMonth(new Date(anchor));
    }
  }, [mode, rangeStart, rangeEnd]);

  const prevYear = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth(), 1)
    );
  const nextYear = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth(), 1)
    );
  const prevMonth = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  const nextMonth = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );

  const handleClearSingle = () => {
    setTempSingle(null);
    props.onChange?.(null);
    closePopoverEvent.emit();
  };

  const handleDayClick = (day: Date) => {
    if (mode === "single") {
      if (tempSingle && isSameDay(day, tempSingle)) {
        setTempSingle(null);
        props.onChange?.(null);
      } else {
        setTempSingle(day);
        props.onChange?.(day);
        closePopoverEvent.emit();
      }
      return;
    }

    if (!tempRange.start || (tempRange.start && tempRange.end)) {
      setTempRange({ start: day, end: null });
    } else if (isSameDay(day, tempRange.start)) {
      setTempRange({ start: null, end: null });
    } else if (day < tempRange.start) {
      setTempRange({ start: day, end: tempRange.start });
    } else {
      setTempRange({ start: tempRange.start, end: day });
    }
  };

  const handleApply = () => {
    props.onRangeChange?.(tempRange.start, tempRange.end);
    closePopoverEvent.emit();
  };

  const handleCancel = () => {
    setTempRange({ start: rangeStart, end: rangeEnd });
    closePopoverEvent.emit();
  };

  const formatDisplay = (): string => {
    if (mode === "single") {
      return singleValue
        ? format(new Date(singleValue), "MMM dd, yyyy")
        : placeholder;
    }
    if (rangeStart && rangeEnd) {
      return `${format(new Date(rangeStart), "MMM dd, yyyy")} – ${format(
        new Date(rangeEnd),
        "MMM dd, yyyy"
      )}`;
    }
    if (rangeStart) {
      return format(new Date(rangeStart), "MMM dd, yyyy");
    }
    return placeholder;
  };

  const days = eachDayOfInterval({
    start: startOfWeek(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)
    ),
    end: endOfWeek(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)
    ),
  });

  return (
    <Popover
      emitter={closePopoverEvent}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      buttonProps={{
        label: formatDisplay(),
        disabled,
        startIcon: <Icon icon="calendar" />,
      }}
    >
      <Box sx={{ p: spacing.md, width: "17rem" }}>
        <CalendarHeader
          currentMonth={currentMonth}
          onPrevYear={prevYear}
          onPrevMonth={prevMonth}
          onNextMonth={nextMonth}
          onNextYear={nextYear}
        />

        <Box sx={{ display: "flex" }}>
          {WEEK_DAYS.map((d) => (
            <Box
              key={d}
              sx={{ width: "14%", textAlign: "center", pb: spacing.xs }}
            >
              <Typography variant="bodySmall" color={color.neutral[400]}>
                {d}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          {days.map((day) => (
            <CalendarDay
              key={day.toISOString()}
              day={day}
              currentMonth={currentMonth}
              mode={mode}
              tempSingle={tempSingle}
              tempRange={tempRange}
              hoverDate={hoverDate}
              onClick={handleDayClick}
              onMouseEnter={setHoverDate}
              onMouseLeave={() => setHoverDate(null)}
            />
          ))}
        </Box>

        {mode === "range" && (
          <Box
            sx={{
              display: "flex",
              gap: spacing.md,
              justifyContent: "flex-end",
              pt: spacing.md,
              mt: spacing.xs,
              borderTop: `1px solid ${color.neutral[200]}`,
            }}
          >
            <Button onClick={handleCancel} size="small" variant="text">
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              variant="contained"
              size="small"
              disabled={!tempRange.start || !tempRange.end}
            >
              Apply
            </Button>
          </Box>
        )}

        {mode === "single" && (
          <Box
            sx={{
              display: "flex",
              gap: spacing.md,
              justifyContent: "flex-end",
              pt: spacing.md,
              mt: spacing.xs,
              borderTop: `1px solid ${color.neutral[200]}`,
            }}
          >
            <Button onClick={handleClearSingle} size="small" variant="text">
              Clear date
            </Button>
          </Box>
        )}
      </Box>
    </Popover>
  );
};
